import { useFormik } from "formik"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import type { TFunction } from "i18next"
import { useHistory } from "react-router-dom"
import * as Yup from "yup"
import { toast } from "react-toastify"

import type { ConfirmationState } from "../../../constants/types"

import { useCreateDestination } from "../../../hooks/useDestinations"
import { useSelectedProject } from "../../../contexts/SelectedProjectContext"
import { useTeams } from "../../../hooks/useTeams"
import kunnat from "../../../assets/kunnat.json"
import Breadcrumb from "../../../components/breadcrumb"
import ConfirmationModal from "../../../components/confirmation"
import Header from "../../../components/header"
import Progressbar from "../../../components/progressbar"
import SearchSelect from "../../../components/searchSelect"
import Tooltip from "../../../components/tooltip"
import { twClassNames } from "../../../helpers/classNames"
import { isLevi } from "../../../helpers/isLevi"

const PHASES = 2
const breadcrumbs = [
  { name: "handbook.create.breadcrumb.home", url: "/handbooks" },
  { name: "handbook.create.breadcrumb.create" }
]

const CONFIRMATION_DEFAULT: ConfirmationState = {
  show: false,
  callback: null,
  danger: false,
  title: "",
  content: ""
}

const validationSchema = (phase: number, t: TFunction) => {
  if (phase === 1) {
    return Yup.object().shape(
      {
        accessCode: Yup.string()
          .min(5, t("common.validation.invalidMinLength"))
          .max(8, t("common.validation.invalidMaxLength"))
          .matches(/^[A-Z0-9]+$/, t("common.validation.onlyUppercaseLettersAndNumbers"))
          .optional(),
        ownerTeamId: Yup.string()
          .min(1, t("common.validation.invalidMinLength"))
          .required(t("common.validation.required")),
        nameFi: Yup.string()
          .when(["nameEn"], {
            is: (nameEn: string) => !nameEn,
            then: () => Yup.string().required(t("common.validation.eitherRequired"))
          })
          .min(3, t("common.validation.invalidMinLength"))
          .max(120, t("common.validation.invalidMaxLength")),
        nameEn: Yup.string()
          .when(["nameFi"], {
            is: (nameFi: string) => !nameFi,
            then: () => Yup.string().required(t("common.validation.eitherRequired"))
          })
          .min(3, t("common.validation.invalidMinLength"))
          .max(120, t("common.validation.invalidMaxLength")),
        streetName: Yup.string()
          .min(3, t("common.validation.invalidMinLength"))
          .max(120, t("common.validation.invalidMaxLength"))
          .required(t("common.validation.required")),
        city: Yup.string()
          .min(3, t("common.validation.invalidMinLength"))
          .max(30, t("common.validation.invalidMaxLength"))
          .required(t("common.validation.required")),
        country: Yup.string()
          .min(3, t("common.validation.invalidMinLength"))
          .max(30, t("common.validation.invalidMaxLength"))
          .required(t("common.validation.required")),
        phone: Yup.string()
          .min(6, t("common.validation.invalidMinLength"))
          .max(30, t("common.validation.invalidMaxLength"))
          .matches(/^\+(?:[0-9]?){6,14}[0-9]$/, t("common.validation.invalidPhone"))
      },
      [["nameFi", "nameEn"]]
    )
  } else if (phase === 2) {
    return Yup.object().shape(
      {
        descriptionFi: Yup.string().when(["descriptionEn"], {
          is: (descriptionEn: string) => !descriptionEn,
          then: () => Yup.string().required(t("common.validation.eitherRequired"))
        }),
        descriptionEn: Yup.string().when(["descriptionFi"], {
          is: (descriptionFi: string) => !descriptionFi,
          then: () => Yup.string().required(t("common.validation.eitherRequired"))
        })
      },
      [["descriptionFi", "descriptionEn"]]
    )
  }
}

interface FormProps {
  t: TFunction
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  formik: any
  isLoading: boolean
  isValidCLass: (field: string) => string | false
  onPrevious: () => void
  onNext: () => void
}

interface Form1Props extends FormProps {
  teams: { name: string; id: string }[]
}

const Form1 = ({ teams, t, formik, isLoading, isValidCLass, onPrevious, onNext }: Form1Props) => (
  <div className="tw-flex tw-p-6 max-md:tw-flex-col">
    {/* Left side */}
    <div className="tw-mb-6 tw-w-full tw-min-w-[300px] max-md:tw-border-b md:tw-w-1/3 md:tw-border-r">
      {/* Basic details */}
      <div className="tw-relative md:tw-pr-6">
        <h5 className="tw-mb-2 tw-text-lg">{t("handbook.create.form.phase1.name")}</h5>
        <p className="tw-text-sm tw-text-dark-75">{t("handbook.create.form.phase1.info")}</p>
      </div>
    </div>
    {/* Right side */}
    <div className="tw-w-full md:tw-w-2/3">
      <div className="tw-flex tw-h-full tw-flex-col md:tw-pl-6">
        <div className="tw-grid-cols tw-grid tw-gap-x-2 tw-gap-y-3 md:tw-grid-cols-6 md:tw-gap-y-4">
          {/* Team */}
          <div className="tw-relative tw-col-span-6 md:tw-col-span-3">
            <label htmlFor="ownerTeamId" className="tw-form-label">
              {t("handbook.create.form.team")}
            </label>
            <SearchSelect
              id="ownerTeamId"
              name="ownerTeamId"
              data={teams.map(team => ({
                name: team.name,
                id: team.id
              }))}
              nameKey="name"
              valueKey="id"
              indexKey="id"
              onChange={value => formik.setFieldValue("ownerTeamId", value)}
              value={formik.values.ownerTeamId}
              errors={formik.touched.ownerTeamId && formik.errors.ownerTeamId}
            />
          </div>
          {/* Accesscode */}
          {isLevi(formik.values.ownerTeamId) && (
            <div className="tw-relative tw-col-span-6 md:tw-col-span-3">
              <label htmlFor="accessCode" className="tw-form-label">
                {t("handbook.create.form.accessCode")}
                <span className="tw-form-label-optional">({t("common.validation.optional")})</span>
              </label>
              <input
                type="text"
                id="accessCode"
                name="accessCode"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.accessCode}
                className={twClassNames(isValidCLass("accessCode"), "tw-form-control tw-uppercase")}
                spellCheck="false"
              />
              <div className="tw-invalid-tooltip">{formik.errors.accessCode}</div>
            </div>
          )}
          {/* Name Fi */}
          <div className="tw-relative tw-col-span-6">
            <label htmlFor="nameFi" className="tw-form-label">
              {t("handbook.create.form.nameFi")}
              <Tooltip message="handbook.create.tooltips.name" displayLabel />
            </label>
            <input
              type="text"
              id="nameFi"
              name="nameFi"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.nameFi ?? ""}
              className={twClassNames(isValidCLass("nameFi"), "tw-form-control")}
              spellCheck="false"
            />
            <div className="tw-invalid-tooltip">{formik.errors.nameFi}</div>
          </div>
          {/* Name En */}
          <div className="tw-relative tw-col-span-6">
            <label htmlFor="nameEn" className="tw-form-label">
              {t("handbook.create.form.nameEn")}
            </label>
            <input
              type="text"
              id="nameEn"
              name="nameEn"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.nameEn ?? ""}
              className={twClassNames(isValidCLass("nameEn"), "tw-form-control")}
              spellCheck="false"
            />
            <div className="tw-invalid-tooltip">{formik.errors.nameEn}</div>
          </div>
          {/* streetName */}
          <div className="tw-relative tw-col-span-6">
            <label htmlFor="streetName" className="tw-form-label">
              {t("handbook.create.form.streetName")}
            </label>
            <input
              type="text"
              id="streetName"
              name="streetName"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.streetName ?? ""}
              className={twClassNames(isValidCLass("streetName"), "tw-form-control")}
              spellCheck="false"
            />
            <div className="tw-invalid-tooltip">{formik.errors.streetName}</div>
          </div>
          {/* City */}
          <div className="tw-relative tw-col-span-6 md:tw-col-span-3">
            <label htmlFor="municipal" className="tw-form-label">
              {t("handbook.create.form.city")}
            </label>
            <SearchSelect
              id="city"
              name="city"
              data={kunnat}
              nameKey="name"
              valueKey="name"
              indexKey="name"
              onChange={value => formik.setFieldValue("city", value)}
              value={formik.values.city}
              errors={formik.touched.city && formik.errors.city}
            />
          </div>
          {/* Country */}
          <div className="tw-relative tw-col-span-6 md:tw-col-span-3">
            <label htmlFor="country" className="tw-form-label">
              {t("handbook.create.form.country")}
            </label>
            <SearchSelect
              id="country"
              name="country"
              data={[{ name: "Suomi", key: "finland" }]}
              nameKey="name"
              valueKey="key"
              indexKey="name"
              onChange={value => formik.setFieldValue("country", value)}
              value={formik.values.country}
              errors={formik.touched.country && formik.errors.country}
            />
          </div>
          {/* Phone */}
          <div className="tw-relative tw-col-span-6 md:tw-col-span-3">
            <label htmlFor="phone" className="tw-form-label">
              {t("handbook.create.form.phone")}
              <span className="tw-form-label-optional">({t("common.validation.optional")})</span>
              <Tooltip message="handbook.create.tooltips.phone" />
            </label>
            <input
              type="phone"
              id="phone"
              name="phone"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.phone ?? ""}
              className={twClassNames(isValidCLass("phone"), "tw-form-control")}
            />
            <div className="tw-invalid-tooltip">{formik.errors.phone}</div>
          </div>
        </div>
        <div className="tw-flex tw-grow tw-place-content-between tw-items-end tw-pt-12">
          <button
            type="button"
            onClick={onPrevious}
            className="tw-btn tw-btn-outline tw-w-full tw-max-w-[180px]"
            disabled={isLoading}>
            {t("handbook.create.button.cancel")}
          </button>
          <button
            type="button"
            onClick={onNext}
            className="tw-btn tw-btn-primary tw-w-full tw-max-w-[180px]"
            disabled={isLoading}>
            {t("handbook.create.button.next")}
          </button>
        </div>
      </div>
    </div>
  </div>
)

const Form2 = ({ t, formik, isLoading, isValidCLass, onPrevious, onNext }: FormProps) => (
  <div className="tw-flex tw-p-6 max-md:tw-flex-col">
    {/* Left side */}
    <div className="tw-mb-6 tw-w-full tw-min-w-[300px] max-md:tw-border-b md:tw-w-1/3 md:tw-border-r">
      {/* Activities informations */}
      <div className="tw-relative md:tw-pr-6">
        <h5 className="tw-mb-2 tw-text-lg">{t("handbook.create.form.phase2.name")}</h5>
        <p className="tw-text-sm tw-text-dark-75">{t("handbook.create.form.phase2.info")}</p>
      </div>
    </div>
    {/* Right side */}
    <div className="tw-w-full md:tw-w-2/3">
      <div className="tw-flex tw-h-full tw-flex-col md:tw-pl-6">
        <div className="tw-grid-cols tw-grid tw-gap-x-2 tw-gap-y-3 md:tw-grid-cols-6 md:tw-gap-y-4">
          {/* Description FI */}
          <div className="tw-relative tw-col-span-6">
            <label htmlFor="descriptionFi" className="tw-form-label">
              {t("handbook.create.form.descriptionFi")}
              <Tooltip message="handbook.create.tooltips.description" displayLabel />
            </label>
            <textarea
              id="descriptionFi"
              name="descriptionFi"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.descriptionFi ?? ""}
              className={twClassNames(isValidCLass("descriptionFi"), "tw-form-control tw-min-h-[110px]")}
              spellCheck="false"
            />
            <div className="tw-invalid-tooltip">{formik.errors.descriptionFi}</div>
          </div>
          {/* Description EN */}
          <div className="tw-relative tw-col-span-6">
            <label htmlFor="descriptionEn" className="tw-form-label">
              {t("handbook.create.form.descriptionEn")}
            </label>
            <textarea
              id="descriptionEn"
              name="descriptionEn"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.descriptionEn ?? ""}
              className={twClassNames(isValidCLass("descriptionEn"), "tw-form-control tw-min-h-[110px]")}
              spellCheck="false"
            />
            <div className="tw-invalid-tooltip">{formik.errors.descriptionEn}</div>
          </div>
        </div>
        <div className="tw-flex tw-grow tw-place-content-between tw-items-end tw-pt-12">
          <button
            type="button"
            onClick={onPrevious}
            className="tw-btn tw-btn-outline tw-w-full tw-max-w-[180px]"
            disabled={isLoading}>
            {t("handbook.create.button.previous")}
          </button>
          <button
            type="button"
            onClick={onNext}
            className="tw-btn tw-btn-primary tw-w-full tw-max-w-[180px]"
            disabled={isLoading}>
            {t("handbook.create.button.save")}
          </button>
        </div>
      </div>
    </div>
  </div>
)

const CreateDestinationContainer = () => {
  const { t } = useTranslation()
  const history = useHistory()
  const { setSelectedDestinationId } = useSelectedProject()
  const createDestinationMutation = useCreateDestination()

  const isLoading = createDestinationMutation.isPending
  const { data: teams = [] } = useTeams()
  const teamsOptions = teams.filter(team => team.userRole === "account_holder" || team.userRole === "admin")

  const [phase, setPhase] = useState(1)
  const [confirmation, setConfirmation] = useState(CONFIRMATION_DEFAULT)

  const formik = useFormik({
    initialValues: {
      country: "finland",
      city: "akaa",
      ownerTeamId: teams[0]?.id ?? ""
    },
    enableReinitialize: true,
    validateOnBlur: false,
    validationSchema: validationSchema(phase, t),
    onSubmit: values => onSave(values)
  })

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onSave = async (values: any) => {
    const data = {
      ...values,
      name: values.nameFi || values.nameEn,
      description: values.descriptionFi || values.descriptionEn
    }
    try {
      const newDestination = await createDestinationMutation.mutateAsync(data)
      setSelectedDestinationId(newDestination.id)
      history.push("/handbook/guides")
    } catch {
      toast.error(t("settings.notifications.save.failed"))
    }
  }

  const onNext = () => {
    formik.validateForm().then(errors => {
      formik.setErrors(errors)
      Object.keys(errors).forEach(key => formik.setFieldTouched(key, true))
      if (!Object.keys(errors).length) {
        if (phase === PHASES) {
          formik.handleSubmit()
        } else {
          setPhase(phase + 1)
        }
      }
    })
  }

  const onPrevious = () => {
    if (phase <= 1) {
      if (formik.dirty) {
        setConfirmation({
          show: true,
          callback: () => history.push("/handbooks"),
          title: "dirty",
          content: "dirty",
          danger: true
        })
      } else {
        history.push("/handbooks")
      }
    } else {
      setPhase(phase - 1)
    }
  }

  const isValidCLass = (field: string) => (formik.touched as Record<string, boolean>)[field] && (formik.errors as Record<string, string>)[field] && "tw-is-invalid"

  return (
    <>
      <Header />
      <div className="tw-has-header tw-mx-auto tw-w-full tw-px-4 xl:tw-container sm:tw-px-12">
        <Breadcrumb data={breadcrumbs} />
        <h1 className="tw-mb-6 tw-font-display tw-text-4xl tw-font-semibold">{t("handbook.create.title")}</h1>
        <div className="tw-mb-12 tw-w-full">
          <div className="tw-mb-6 tw-rounded tw-bg-white tw-shadow">
            <form noValidate onSubmit={formik.handleSubmit}>
              {(() => {
                switch (phase) {
                  case 1:
                    return (
                      <Form1
                        t={t}
                        teams={teamsOptions}
                        formik={formik}
                        isLoading={isLoading}
                        isValidCLass={isValidCLass}
                        onPrevious={onPrevious}
                        onNext={onNext}
                      />
                    )
                  case 2:
                    return (
                      <Form2
                        t={t}
                        formik={formik}
                        isLoading={isLoading}
                        isValidCLass={isValidCLass}
                        onPrevious={onPrevious}
                        onNext={onNext}
                      />
                    )
                  default:
                    return null
                }
              })()}
            </form>
          </div>
        </div>
        <div className="tw-mb-12 tw-w-full">
          <Progressbar phases={PHASES} current={phase} translationKeys="handbook.create.progress" />
        </div>
        <ConfirmationModal
          show={confirmation.show}
          title={confirmation.title}
          content={confirmation.content}
          danger={confirmation.danger}
          onOK={confirmation.callback}
          onCancel={() => setConfirmation(CONFIRMATION_DEFAULT)}
        />
      </div>
    </>
  )
}

export default CreateDestinationContainer
